import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, CircleCheck, Copy, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { copyToClipboard } from '../../lib/clipboard';
import { errorText } from '../../lib/apiErrors';
import { createWebhook, deleteWebhook, updateWebhook } from '../../lib/developerApi';

const HTTPS = /^https:\/\/[^\s/]+\.[^\s]+/;

/**
 * Add (or edit) a webhook endpoint: URL, optional description, events.
 *
 * The server verifies the URL on save — a webhook platform URL (Zapier, Make,
 * n8n, …) is accepted as is, any other must echo the `nurturecrm.verify`
 * challenge — so a refusal comes back as a message shown inline. On add the
 * response carries the signing secret, handed to `onCreated` for the
 * show-once dialog.
 */
export function EndpointDialog({ open, endpoint, catalog, headers, onClose, onCreated, onSaved }) {
    const editing = Boolean(endpoint);
    const [url, setUrl] = useState('');
    const [description, setDescription] = useState('');
    const [events, setEvents] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const wasOpen = useRef(false);

    useEffect(() => {
        if (open && !wasOpen.current) {
            setUrl(endpoint?.url || '');
            setDescription(endpoint?.description || '');
            setEvents(endpoint?.events || []);
            setError('');
        }
        wasOpen.current = open;
    }, [open, endpoint]);

    const toggle = (type) => setEvents((list) => (list.includes(type) ? list.filter((e) => e !== type) : [...list, type]));
    const valid = HTTPS.test(url.trim()) && events.length > 0;
    const hint = !HTTPS.test(url.trim())
        ? 'Enter an HTTPS address.'
        : !events.length
            ? 'Pick at least one event.'
            : editing
                ? 'A changed URL is verified again.'
                : 'The signing secret is shown once, after you add the endpoint.';

    const submit = async () => {
        if (!valid || saving) return;
        setSaving(true);
        setError('');
        try {
            if (editing) {
                const body = { events };
                if (url.trim() !== endpoint.url) body.url = url.trim();
                if (description.trim() !== (endpoint.description || '')) body.description = description.trim();
                onSaved(await updateWebhook(headers, endpoint.endpoint_id, body));
            } else {
                onCreated(await createWebhook(headers, {
                    url: url.trim(), events, description: description.trim() || null,
                }));
            }
        } catch (err) {
            setError(errorText(err, 'Could not save the endpoint.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent className="sm:max-w-xl" data-testid="endpoint-dialog">
                <DialogHeader>
                    <DialogTitle>{editing ? 'Edit webhook endpoint' : 'Add webhook endpoint'}</DialogTitle>
                    <DialogDescription>We’ll send a signed POST to this address for each event you pick.</DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <label className="flex flex-col gap-1.5 text-sm font-medium text-stone-900">
                        Endpoint URL
                        <input
                            className="h-9 px-3 border border-stone-200 rounded-md bg-white font-mono text-[13px] font-normal focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://your-app.com/webhooks/nurturecrm"
                            autoComplete="off"
                            data-testid="endpoint-url"
                        />
                        <span className="text-xs font-normal text-stone-500">
                            Must be HTTPS and reachable from the internet. Zapier, Make, n8n, Slack, Teams, Discord and Pipedream URLs work as they are; any other endpoint must return the <span className="font-mono">challenge</span> value we send it.
                        </span>
                    </label>
                    <label className="flex flex-col gap-1.5 text-sm font-medium text-stone-900">
                        <span>Description <span className="font-normal text-stone-400">(optional)</span></span>
                        <input
                            className="h-9 px-3 border border-stone-200 rounded-md bg-white text-sm font-normal focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            value={description}
                            maxLength={200}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="e.g. Velocity lead sync"
                        />
                    </label>
                    <fieldset className="flex flex-col gap-1.5">
                        <legend className="text-sm font-medium text-stone-900 mb-1.5">Events</legend>
                        <div className="border border-stone-200 rounded-lg divide-y divide-stone-100 max-h-72 overflow-y-auto">
                            {catalog.map((e) => {
                                const on = events.includes(e.type);
                                return (
                                    <label key={e.type} className="flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-stone-50">
                                        <input
                                            type="checkbox"
                                            className="sr-only"
                                            checked={on}
                                            onChange={() => toggle(e.type)}
                                            data-testid={`endpoint-event-${e.type}`}
                                        />
                                        <span aria-hidden className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center ${on ? 'bg-stone-900 border-stone-900 text-white' : 'border-stone-400 bg-white'}`}>
                                            {on && <Check className="w-3 h-3" />}
                                        </span>
                                        <span>
                                            <span className="block font-mono text-[13px] text-stone-900">{e.type}</span>
                                            <span className="block text-[12.5px] text-stone-500">{e.description}</span>
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </fieldset>
                    {error && (
                        <div className="flex gap-2 items-start text-[13px] px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-800" data-testid="endpoint-error">
                            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{error}</span>
                        </div>
                    )}
                </div>
                <DialogFooter className="items-center gap-2 sm:justify-between">
                    <span className="text-[12.5px] text-stone-500">{hint}</span>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="h-9 px-4 rounded-md border border-stone-200 text-sm font-medium hover:bg-stone-50">Cancel</button>
                        <button
                            type="button"
                            disabled={!valid || saving}
                            onClick={submit}
                            data-testid="endpoint-save"
                            className="h-9 px-4 rounded-md bg-stone-900 text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-stone-800 disabled:bg-stone-200 disabled:text-stone-400"
                        >
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            {editing ? 'Save endpoint' : 'Add endpoint'}
                        </button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * The signing secret, shown once — after adding an endpoint or rotating its
 * secret. Like the API key reveal, it can only be closed with its own button,
 * and nothing keeps the secret after it closes.
 */
export function SecretDialog({ secret, rotated, onClose }) {
    const contentRef = useRef(null);
    if (!secret) return null;
    const copy = async () => {
        if (await copyToClipboard(secret, contentRef.current)) toast.success('Signing secret copied');
        else toast.error('Select the secret and copy it manually');
    };
    return (
        <Dialog open onOpenChange={() => {}}>
            <DialogContent
                className="sm:max-w-lg"
                hideClose
                ref={contentRef}
                data-testid="secret-dialog"
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CircleCheck className="w-5 h-5 text-emerald-700" /> {rotated ? 'New signing secret' : 'Endpoint added'}
                    </DialogTitle>
                    <DialogDescription>
                        Use this secret in your app to check that events really come from NurtureCRM.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2.5 bg-stone-50 border border-dashed border-violet-600 rounded-lg px-3.5 py-3">
                        <code className="flex-1 font-mono text-[12.5px] break-all select-all" data-testid="secret-value">{secret}</code>
                        <button type="button" onClick={copy} className="h-8 px-3 rounded-md border border-stone-200 bg-white text-[13px] font-medium inline-flex items-center gap-1.5 hover:bg-stone-50">
                            <Copy className="w-3.5 h-3.5" /> Copy
                        </button>
                    </div>
                    <div className="flex gap-2 items-start text-[13px] px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>
                            Copy it now. It won’t be shown again; you can rotate it later.
                            {rotated && ' Events are signed with both the new and the old secret for the next 24 hours, so you can switch without missing any.'}
                        </span>
                    </div>
                </div>
                <DialogFooter>
                    <button type="button" onClick={onClose} data-testid="secret-done" className="h-9 px-4 rounded-md bg-stone-900 text-white text-sm font-medium hover:bg-stone-800">
                        Done
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** Confirm removing an endpoint; what it has not received yet is cancelled. */
export function RemoveEndpointDialog({ endpoint, headers, onClose, onRemoved }) {
    const [busy, setBusy] = useState(false);
    if (!endpoint) return null;
    const remove = async () => {
        setBusy(true);
        try {
            await deleteWebhook(headers, endpoint.endpoint_id);
            toast.success('Endpoint removed');
            onRemoved();
        } catch (err) {
            toast.error(errorText(err, 'Could not remove the endpoint.'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent className="sm:max-w-md" data-testid="remove-endpoint-dialog">
                <DialogHeader>
                    <DialogTitle>Remove this endpoint?</DialogTitle>
                    <DialogDescription>
                        <span className="font-mono text-[12.5px] break-all">{endpoint.url}</span> stops receiving events now. Events it has not received yet are cancelled.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2">
                    <button type="button" onClick={onClose} className="h-9 px-4 rounded-md border border-stone-200 text-sm font-medium hover:bg-stone-50">Cancel</button>
                    <button type="button" disabled={busy} onClick={remove} data-testid="remove-endpoint-confirm" className="h-9 px-4 rounded-md bg-red-600 text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-red-700 disabled:opacity-60">
                        <Trash2 className="w-4 h-4" /> Remove endpoint
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
